import { DatePipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { APPOINTMENT_CONSTANT } from '../../../../constants/appointment-constants';
import { DATE_TIME_ISO_T_FORMAT } from '../../../../constants/date.constants';
import { TEMPLATE_CONSTANT } from '../../../../constants/template.constant';
import { TRANSLATION_TEMPLATES } from '../../../../constants/translation-templates-constants';
import { VAT_TYPE } from '../../../../constants/widget-constants';
import { CustomFieldsDao } from '../../../../db-models/appointment-custom-fields-dao';
import { AppointmentServiceDao } from '../../../../db-models/appointment-service-dao';
import { CouponDbModel } from '../../../../db-models/coupon-db.model';
import { AppointmentsSlotsDao } from '../../../../db-models/free-appointment-dao';
import { StoresDao } from '../../../../db-models/stores-dao';
import { CustomBookingMessageTemplate } from '../../../../db-models/widget-conf-dao';
import { CartItem } from '../../../../models/cart.model';
import { GlobalObjects, Partner } from '../../../../models/global';
import { AppointmentState, ConferenceState, UserState } from '../../../../models/state.model';
import { AppointmentService } from '../../../../services/appointment.service';
import { CustomFieldService } from '../../../../services/custom-field.service';
import { DateUtilsService } from '../../../../services/date-utils.service';
import { FormsService } from '../../../../services/forms.service';
import { HelperService } from '../../../../services/helper.service';
import { LoggerService } from '../../../../services/logger.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { UtilService } from '../../../../services/util.service';
import { TaxableLine, VatResult, calculateIncludedVat, calculateVat, isVatAddedOnTop, vatTypeLabelKey } from '../../../../services/vat-calculation.util';
import { NgxClampComponent } from '../../../../shared/components/ngx-clamp/ngx-clamp.component';
import { AddressFormatPipe } from '../../../../shared/pipes/address-format.pipe';
import { CalioCurrencyPipe } from '../../../../shared/pipes/calio-currency.pipe';
import { CalioMeetingTemplatePipe } from '../../../../shared/pipes/calio-meeting-template.pipe';
import { DateUtcPipe } from '../../../../shared/pipes/date_utc.pipe';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../../shared/pipes/trust-html.pipe';
import { CustomfieldsComponent } from '../../../common/customfields/customfields.component';
import { CwCardHeaderComponent } from '../../../common/theme/cw-card-header/cw-card-header.component';
import { CwNewCardComponent } from '../../../common/theme/cw-new-card/cw-new-card.component';

const pipes = [AddressFormatPipe, TranslationPipe, TrustHtmlPipe, CalioCurrencyPipe, CalioMeetingTemplatePipe, DatePipe, DateUtcPipe];
const components = [CwNewCardComponent, CwCardHeaderComponent, NgxClampComponent, CustomfieldsComponent];
const modules = [NgClass, TranslateModule];

@Component({
  selector: 'app-appointment-cart-overview',
  templateUrl: './appointment-cart-overview.component.html',
  styleUrls: ['./appointment-cart-overview.component.scss'],
  imports: [...modules, ...components, ...pipes]
})
export class AppointmentCartOverviewComponent implements OnInit, OnChanges {
  protected readonly vatTypeLabelKey = vatTypeLabelKey;

  private appointmentService = inject(AppointmentService);
  private customFieldService = inject(CustomFieldService);
  private dateUtilsService = inject(DateUtilsService);
  private formService = inject(FormsService);
  private helperService = inject(HelperService);
  private timezoneService = inject(TimezoneService);
  private translateService = inject(TranslateService);
  private utilService = inject(UtilService);

  @Input() cart: CartItem[];
  @Input() partner: Partner;
  @Input() totalCartDuration: number;
  @Input() set customFieldsAddonsPrice(value: number) {
    this._customFieldsAddonsPrice = value;
  }
  @Input() set totalCartPrice(value: number) {
    this._totalCartPrice = value;
  }
  @Input() appointmentServicesAvailable: AppointmentServiceDao[];
  @Input() lang: string;
  @Input() coupon: CouponDbModel;
  @Input() finalCouponDiscount = 0;
  @Input() showTotalBlock = false;
  @Input() courtesyForm: string;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() appointmentState: AppointmentState;
  @Input() stores: StoresDao[] = [];
  @Input() globals: GlobalObjects;
  @Input() userState: UserState;
  @Input() conferenceState: ConferenceState;
  @Input() mode = APPOINTMENT_CONSTANT.PERSONAL_INFO;
  @Input() customFields: CustomFieldsDao[];
  @Input() selectedJsonCustomFields: {
    [key: number]: { type: string; value: any } | any;
  };
  @Input() customFieldValues: {
    [key: number]: {
      type: string, value: any, option_values: {
        custom_field_option_id: number, custom_field_option_value: string,
      }[]
    } | any
  };
  @Input() customFieldValuesLSNameKey: string;

  @Output() removeFromCartEvent = new EventEmitter<string>();
  @Output() updateCartEvent = new EventEmitter<any>();

  get timeFormat(): string {
    return this.dateUtilsService.userTimeFormat.moment;
  }

  readonly dateTimeIsoTFormat = DATE_TIME_ISO_T_FORMAT;
  readonly templateContent = TRANSLATION_TEMPLATES;
  readonly appointmentConstant = APPOINTMENT_CONSTANT;
  protected readonly vatType = VAT_TYPE;
  protected readonly templateConstant = TEMPLATE_CONSTANT;
  protected readonly isVatAddedOnTop = isVatAddedOnTop;

  finalTotal: number;
  /** VAT summary (one entry per rate) on the discounted net — empty for `inclusive` partners. */
  vat = signal<VatResult>({ byRate: [], totalCents: 0 });
  /** VAT already contained in the prices (one entry per rate) — `inclusive` partners only, display only. */
  containedVat = signal<VatResult>({ byRate: [], totalCents: 0 });
  templateTitle: CustomBookingMessageTemplate;
  templateSubTitle: CustomBookingMessageTemplate;
  relatedAppointmentServices: {
    relatedAppointmentCardId: number,
    relatedServiceId: number,
    relatedServiceData: AppointmentServiceDao,
    originalAppointmentService: AppointmentServiceDao,
    beforeDates: AppointmentsSlotsDao[],
    afterDates: AppointmentsSlotsDao[],
    allDates: AppointmentsSlotsDao[],
    isAddedToCart: boolean,
  }[] = [];
  customCardClass = '';
  protected _totalCartPrice: number;
  protected _customFieldsAddonsPrice: number;

  constructor() {
    this.translateService.onLangChange.subscribe(language => this.lang = language.lang);
  }

  ngOnInit() {
    if (
      this.partner.split_personal_form_and_summary_in_widget === 1 &&
      this.mode === this.appointmentConstant.PERSONAL_INFO
    ) {
      this.mode = this.globals.isInternal ? this.appointmentConstant.PERSONAL_INFO : this.appointmentConstant.SUMMARY;
    }

    this.getRelatedServiceFreeAppointments();
    this.customCardClass += this.mode === this.appointmentConstant.SUMMARY ? ' p-0' : '';
  }

  getServicesByIds({ serviceIds, returnKey = 'services' }: { serviceIds: number[]; returnKey?: string }) {
    return this.appointmentService.getServicesByIds({ serviceIds, returnKey }, this.appointmentServicesAvailable);
  }

  removeFromCart(cartItemId: string, relatedAppointmentCardId: number) {
    if (this.relatedAppointmentServices?.length && relatedAppointmentCardId) {
      for (const relatedAppointmentService of this.relatedAppointmentServices) {
        if (Number(relatedAppointmentService?.relatedAppointmentCardId) === relatedAppointmentCardId) {
          relatedAppointmentService.isAddedToCart = false;
        }
      }
    } else {
      // Check for related service after 1 second delay as we need to wait this.cart variable to be updated
      setTimeout(() => this.getRelatedServiceFreeAppointments(), 1000);
    }

    this.removeFromCartEvent.next(cartItemId);
  }

  getFinalTotal(): number {
    if (this._totalCartPrice > 0) {
      const netAfterCoupon = Number(this._totalCartPrice) - Number(this.finalCouponDiscount);
      const vatResult = this.calculateCartVat(netAfterCoupon);
      this.vat.set(vatResult);
      this.containedVat.set(this.calculateCartVat(netAfterCoupon, true));
      this.finalTotal = Number((netAfterCoupon + vatResult.totalCents / 100).toFixed(2));
      return this.finalTotal;
    } else {
      this.vat.set({ byRate: [], totalCents: 0 });
      this.containedVat.set({ byRate: [], totalCents: 0 });
      this.finalTotal = 0;
      return this.finalTotal;
    }
  }

  protected subtotalLabelKey(): string {
    return this.showsVatRows() ? 'tax_info.net_total' : 'summary_page_translations.subtotal';
  }

  protected totalLabelKey(): string {
    if (vatTypeLabelKey(this.partner?.widget_vat_type) === 'exclude') {
      return 'tax_info.amount_excl_tax';
    }
    return this.showsVatRows() ? 'tax_info.amount_incl_tax' : 'appointments.step4.total';
  }

  /** The VAT wording only applies when VAT is added on top or the prices actually contain some. */
  private showsVatRows(): boolean {
    return isVatAddedOnTop(this.partner?.widget_vat_type) || this.containedVat().byRate.length > 0;
  }

  /**
   * Net-after-coupon → VAT for the whole cart: on top for `plus`, or with `contained` the share the
   * prices already hold for `inclusive`. Empty/zero otherwise.
   */
  private calculateCartVat(discountedNet: number, contained = false): VatResult {
    const vatType = this.partner?.widget_vat_type;
    if (contained ? vatTypeLabelKey(vatType) !== 'inclusive' : !isVatAddedOnTop(vatType)) {
      return { byRate: [], totalCents: 0 };
    }
    const lines: TaxableLine[] = (this.cart || []).reduce(
      (acc, item) => acc.concat(item.total?.taxableLines || []), [] as TaxableLine[]
    );
    const discountedNetCents = Math.round(discountedNet * 100);
    const undiscountedNetCents = Math.round(Number(this._totalCartPrice) * 100);
    return (contained ? calculateIncludedVat : calculateVat)(lines, discountedNetCents, undiscountedNetCents);
  }

  /** Per-item `+ amount (plus X% VAT)` row — ratio 1 (no cross-item coupon allocation here). */
  getItemVatAmount(cartItem: CartItem, itemNet: number): number {
    if (!isVatAddedOnTop(this.partner?.widget_vat_type) || !(itemNet > 0)) {
      return 0;
    }
    const itemNetCents = Math.round(itemNet * 100);
    const result = calculateVat(cartItem.total?.taxableLines || [], itemNetCents, itemNetCents);
    return result.totalCents / 100;
  }

  /**
   * Rate label for the per-item row. `total.tax` only keeps the LAST selected service's rate, so a
   * multi-service item with mixed rates would show one rate next to a VAT amount that covers all
   * of them — list every distinct rate of the item's own lines instead, in selection order.
   */
  getItemVatRates(cartItem: CartItem): string {
    const rates = (cartItem.total?.taxableLines || []).map(line => line.rateHundredths / 100);
    const distinct = rates.filter((rate, index) => rates.indexOf(rate) === index);
    if (!distinct.length) {
      return `${cartItem.total?.tax ?? 0}%`;
    }
    return distinct.map(rate => `${rate}%`).join(' / ');
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.getFinalTotal();
    this.customFieldService.addonsCustomFieldsPriceEvent.subscribe(() => {
      this.getFinalTotal();
    });

    if (changes?.widgetTemplates?.currentValue) {
      this.widgetTemplates?.length && this.setupTemplates();
    }
  }

  setupTemplates(): void {
    this.templateTitle = this.widgetTemplates.find(template => template.id === 124);
    this.templateTitle && (this.templateTitle.is_multi_language = 1);
    this.templateSubTitle = this.widgetTemplates.find(template => template.id === 125);
    this.templateSubTitle && (this.templateSubTitle.is_multi_language = 1);
  }

  getAppointmentServiceById(serviceId: number): AppointmentServiceDao {
    return this.appointmentServicesAvailable?.find(service => service?.id === serviceId);
  }

  getRelatedServiceFreeAppointments(): void {
    const relatedServices = this.cart[0]?.relatedServiceIds?.length ? this.cart[0]?.relatedServiceIds : [];
    if (relatedServices?.length) {
      const relatedServiceAlreadyPresentInCart = this.cart.find(service => service.servicesIds.includes(relatedServices[0]));
      if (!relatedServiceAlreadyPresentInCart) {
        // As of now sending only 1 related service
        this.getFreeAppointments(
          this.appointmentState.day.date,
          0,
          relatedServices[0]?.toString(),
          this.appointmentState.store,
          this.cart[0].servicesIds[0],
          this.conferenceState?.uuid || undefined
        );
      }
    }
  }

  getFreeAppointments(
    date: Date,
    workerId: number,
    appointmentIds: string,
    storeId: number,
    parentServiceId = 0,
    conferenceUuid = undefined
  ) {
    this.formService.getDaysWiseFreeSlots(
      workerId,
      storeId,
      appointmentIds,
      this.utilService.dateToStr(date),
      this.globals.isInternal,
      conferenceUuid,
      this.globals.debugMode,
      this.globals.internalToken,
      undefined,
      undefined,
      undefined,
      this.partner?.is_timezone_enabled ? (this.userState?.timezone ?? this.timezoneService.localTimezone) : undefined
    ).subscribe({
      next: freeAppointments => {
        let availableSlotsArray: {
          [key: string]: AppointmentsSlotsDao[]
        }[] = [];

        if (freeAppointments && !('errors' in freeAppointments)) {
          // preparing array that has day wise availabilites
          freeAppointments.available_minutes_blocks?.forEach(daysWiseSlots => {
            daysWiseSlots.workers_minutes_blocks?.forEach(workerMinutesBlock => {
              workerMinutesBlock.minutes_blocks.forEach(availabilities => {
                availabilities.appointments_slots?.forEach(slot => {
                  if (!slot.is_reserved) {
                    let workerUuids = Array<string>();
                    if (this.conferenceState?.uuid === undefined && workerId === 0) {
                      workerUuids = [workerMinutesBlock.worker.uuid];
                    } else {
                      workerUuids = workerMinutesBlock.workers.map(worker => worker.uuid);
                    }

                    !availableSlotsArray[daysWiseSlots.date] && (availableSlotsArray[daysWiseSlots.date] = []);
                    availableSlotsArray[daysWiseSlots.date].push({
                      start: slot.appointment_start,
                      end: slot.appointment_end,
                      worker: workerMinutesBlock.worker,
                      workerUuids,
                      store: workerMinutesBlock.store,
                      startHour: parseInt(slot.appointment_start.split(' ')[1].split(':')[0], 10),
                      short_start: slot.appointment_short_start,
                      location: slot.location,
                      isReserved: slot.is_reserved,
                      ...(parentServiceId ? { parentServiceId } : {})
                    });
                  }
                });
              });
            });
          });

          Object.keys(availableSlotsArray)?.length && this.setupSlots(availableSlotsArray);
        }
      },
      error: (error: HttpErrorResponse) => LoggerService.error(error)
    });
  }

  setupSlots(freeAppointments: { [key: string]: AppointmentsSlotsDao[]; }[]) {
    this.cart?.forEach(cart => {
      cart?.relatedServices?.forEach(relatedService => {
        const beforeDates: AppointmentsSlotsDao[] = [];
        const afterDates: AppointmentsSlotsDao[] = [];
        let allDates: AppointmentsSlotsDao[] = [];
        let finalDates: AppointmentsSlotsDao[] = [];

        Object.keys(freeAppointments).forEach(date => {
          freeAppointments[date]?.forEach((freeAppointment: AppointmentsSlotsDao) => {
            if (moment(freeAppointment.end, this.dateTimeIsoTFormat).isSame(moment(cart.startTime, this.dateTimeIsoTFormat))) {
              beforeDates.push(freeAppointment);
            }

            if (moment(freeAppointment.start, this.dateTimeIsoTFormat).isSame(moment(cart.endTime, this.dateTimeIsoTFormat))) {
              afterDates.push(freeAppointment);
            }
          });
        });


        beforeDates?.length && (allDates = allDates.concat(beforeDates));
        afterDates?.length && (allDates = allDates.concat(afterDates));

        if (allDates?.length) {

          // Check suggestions are getting conflicted with other items of cart
          allDates.forEach(date => {
            let isConflicts = false;
            for (const cartItem in this.cart) {
              if (
                date.start > this.cart[cartItem]['startTime'] && date.start < this.cart[cartItem]['endTime'] ||
                date.start <= this.cart[cartItem]['startTime'] && date.end >= this.cart[cartItem]['startTime']
              ) {
                isConflicts = true;
                break;
              }
            }

            if (!isConflicts) {
              finalDates.push(date);
            }
          });

          if (finalDates?.length) {
            this.relatedAppointmentServices.push({
              relatedAppointmentCardId: this.helperService.random(1000000, 1),
              relatedServiceId: relatedService?.id,
              relatedServiceData: relatedService,
              originalAppointmentService: this.getAppointmentServiceById(relatedService?.related_appointment_service_id),
              beforeDates,
              afterDates,
              allDates: finalDates,
              isAddedToCart: false,
            });
          }
        }
      });
    });
  }

  onSelectSlot(
    relatedAppointmentService: {
      relatedAppointmentCardId: number,
      relatedServiceId: number,
      relatedServiceData: AppointmentServiceDao,
      originalAppointmentService: AppointmentServiceDao,
      beforeDates: AppointmentsSlotsDao[],
      afterDates: AppointmentsSlotsDao[],
      allDates: AppointmentsSlotsDao[],
      isAddedToCart: boolean,
    },
    freeAppointment: AppointmentsSlotsDao,
  ) {
    relatedAppointmentService.isAddedToCart = true;
    this.updateCartEvent.emit({
      event: { target: { checked: true } },
      appointment: freeAppointment,
      ignoreRelatedServiceId: true,
      ignoreAppointmentStateServices: true,
      relatedServiceId: relatedAppointmentService?.relatedServiceId,
      relatedAppointmentCardId: relatedAppointmentService?.relatedAppointmentCardId,
      refreshFormAndCartData: true,
    });
  }
}
